import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef } from 'react'
import { useMotionValueEvent, useScroll } from 'motion/react'
import { cn } from '@/lib/utils'

interface DayClockProps {
    className?: string
}

type ClockAnchor = {
    top: number
    minutes: number
}

const DAY = 24 * 60

const toMinutes = (value: string): number => {
    const [time = '00:00', days = '0'] = value.split('+')
    const [hours = 0, minutes = 0] = time.split(':').map(part => Number.parseInt(part, 10) || 0)

    return hours * 60 + minutes + (Number.parseInt(days, 10) || 0) * DAY
}

const labelFor = (minutes: number): string => {
    if (minutes >= 300 && minutes < 660) return 'Morning'
    if (minutes >= 660 && minutes < 840) return 'Midday'
    if (minutes >= 840 && minutes < 1080) return 'Afternoon'
    if (minutes >= 1080 && minutes < 1260) return 'Golden hour'
    if (minutes >= 1260 && minutes < 1350) return 'Dusk'

    return 'Night'
}

/**
 * A day at the space. Sections mark a time with data-clock ("21:22", or "00:48+1"
 * for the next day), and the clock runs between them as the page scrolls.
 * The anchored times are when the venue photos on the page were taken.
 */
export const DayClock: FC<DayClockProps> = ({ className }): ReactNode => {
    const timeRef = useRef<HTMLSpanElement>(null)
    const labelRef = useRef<HTMLSpanElement>(null)
    const handRef = useRef<SVGGElement>(null)
    const anchors = useRef<Array<ClockAnchor>>([])
    const { scrollY } = useScroll()

    const paint = useCallback((y: number) => {
        const list = anchors.current

        if (!list.length) return

        const probe = y + window.innerHeight * 0.5
        const last = list[list.length - 1]
        let minutes = list[0].minutes

        if (probe >= last.top) {
            minutes = last.minutes
        }
        else {
            for (let index = 0; index < list.length - 1; index++) {
                const from = list[index]
                const to = list[index + 1]

                if (probe >= from.top && probe < to.top) {
                    const progress = (probe - from.top) / Math.max(1, to.top - from.top)

                    minutes = from.minutes + (to.minutes - from.minutes) * progress
                    break
                }
            }
        }

        const total = ((Math.floor(minutes) % DAY) + DAY) % DAY
        const hours = String(Math.floor(total / 60)).padStart(2, '0')
        const mins = String(total % 60).padStart(2, '0')

        if (timeRef.current) timeRef.current.textContent = `${hours}:${mins}`
        if (labelRef.current) labelRef.current.textContent = labelFor(total)
        if (handRef.current) handRef.current.style.transform = `rotate(${(total / DAY) * 360}deg)`
    }, [])

    useEffect(() => {
        const measure = () => {
            anchors.current = Array.from(document.querySelectorAll<HTMLElement>('[data-clock]'))
                .map(element => ({
                    top: element.getBoundingClientRect().top + window.scrollY,
                    minutes: toMinutes(element.dataset.clock ?? '00:00')
                }))
                .sort((a, b) => a.top - b.top)

            paint(window.scrollY)
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(document.body)

        return () => observer.disconnect()
    }, [paint])

    useMotionValueEvent(scrollY, 'change', paint)

    return (
        <div className={cn('flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em]', className)} aria-hidden='true'>
            <svg viewBox='0 0 16 16' className='size-4 shrink-0' fill='none' stroke='currentColor' strokeWidth='1'>
                <circle cx='8' cy='8' r='7.25' />
                <g ref={handRef} style={{ transformBox: 'view-box', transformOrigin: '50% 50%', transform: 'rotate(117.75deg)' }}>
                    <path d='M8 8V2.75' strokeLinecap='round' />
                </g>
                <circle cx='8' cy='8' r='1' fill='currentColor' stroke='none' />
            </svg>

            <span ref={timeRef} className='tabular-nums'>07:51</span>
            <span className='h-px w-6 bg-current opacity-40' />
            <span ref={labelRef} className='text-tone-muted'>Morning</span>
        </div>
    )
}
